import { createSlice } from "@reduxjs/toolkit";
import { Models, NQHint, createNqError } from "@visonum/network-quality-sdk";
import { format } from "date-fns";
import FileSaver from "file-saver";
import { AppThunk, RootState } from "../../store";
import { reportNQError } from "../errors/errorSlice";
import { logger } from '../../helper/logger';
import { getSdk } from '../../networkQuality';
import { lastValueFrom } from 'rxjs';
import { createPrintRequestModel } from './createPrintRequestModel';
import { NQRecord } from '../history/types';
import { hintIdsToHintObjects, serverHintsToClientHintIds } from "../hints/hintFunctions";
import { getCurrentLang } from "../../helper/utils";
import { PartialServiceState, getInputFromNqRecord, getPartialServiceState } from "../partialService/partialServiceUtils";
import { LangName } from "../../types";
import getSpeedtestId from "../../helper/utils/getSpeedtestId";

export enum PrintStage {
    None = "None",
    Request = "Request",
    SucceededResponse = "SucceededResponse",
}

export type PrintState = {
    kind: PrintStage.None,
} | {
    kind: PrintStage.Request,
} | {
    kind: PrintStage.SucceededResponse,
}

const initialState = {
    kind: PrintStage.None
} as PrintState;

const getTips = (lang: LangName, partialServiceState: PartialServiceState, record: NQRecord) => {
    if (record.hints === null) {
        return null;
    }

    const nqHints = serverHintsToClientHintIds(record.hints.map(id => ({ id } as NQHint)), partialServiceState);
    return hintIdsToHintObjects(nqHints, record, lang)
        .map(h => ({
            id: h.id,
            title: h.title,
            teaserTitle: h.teaserTitle,
            teaser: h.teaser,
            contentTitle: h.contentTitle,
            content: h.content,
        }));
}

const printRequest = async (state: RootState, record: NQRecord): Promise<Blob> => {
    const lang = getCurrentLang();
    const tips = getTips(lang, getPartialServiceState(getInputFromNqRecord(record)), record);
    const printRequestModel = createPrintRequestModel(record, lang as Models.LanguageKey, tips);

    try {
        return await lastValueFrom(getSdk().print(printRequestModel));
    } catch (err) {
        logger.error("printRequest", err);
        throw err;
    }
}

export const printReport = (record: NQRecord): AppThunk<Promise<void>> => async (dispatch, getState) => {
    if (getState().print.kind !== PrintStage.None) {
        return;
    }
    dispatch(printSlice.actions.request());
    try {
        const blob = await printRequest(getState(), record);
        dispatch(printSlice.actions.succeededResponse());
        const fileName = `speedtestplus-${format(record.date, "yyyy-MM-dd")}-${getSpeedtestId(record.prepareResult)}.pdf`;
        logger.info(`Saving file: ${fileName}`);
        FileSaver.saveAs(blob, fileName);
        dispatch(printSlice.actions.reset());
    } catch (err) {
        dispatch(reportNQError(createNqError("Cl-12-130", "Print Report Saving Error", err)));
        dispatch(printSlice.actions.reset());
    }
}

export const printSlice = createSlice({
    name: 'print',
    initialState,
    reducers: {
        reset: (state) => { state.kind = PrintStage.None },
        request: (state) => { state.kind = PrintStage.Request },
        succeededResponse: (state) => { state.kind = PrintStage.SucceededResponse },
    },
});

export const { } = printSlice.actions;

export default printSlice.reducer;